import { inject, Injectable } from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { ApiClientConstant } from '@cureskin/api-client';
import { ConnectionService } from '@services/connection-service';
import { ConsultationSessionService } from '@services/consultation-session-service';
import { EventLoggerService } from '@services/event-logger-service';
import { LanguageChangeComponent } from '@shared/bottom-sheet-layouts/language-change/language-change.component';
import { AppConfig } from 'client/app/app.config';

export interface LanguageBannerContent {
  currentLanguageLabel: string;
  currentLanguageCode: string;
}

@Injectable({ providedIn: 'root' })
export class LanguageBannerService {
  private readonly conn = inject(ConnectionService);
  private readonly consultationSessionService = inject(ConsultationSessionService);
  private readonly eventLogger = inject(EventLoggerService);
  private readonly bottomSheet = inject(MatBottomSheet);
  private readonly appConfig = inject(AppConfig);

  async buildLanguageBannerContent(): Promise<LanguageBannerContent | null> {
    const chatPending = await this.consultationSessionService.isIncompleteChatPending();
    if (!chatPending) return null;

    const user = this.conn.getActingUser();
    const currentLanguageCode = user?.get('languagePreference') || this.appConfig.Shared.Languages.EN;

    return {
      currentLanguageLabel: this.appConfig.Shared.LanguagesLong[currentLanguageCode.toUpperCase()],
      currentLanguageCode,
    };
  }

  openLanguageSelector(currentLanguageCode: string): void {
    this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'home-language-banner-tap', pageName: 'home' }));
    const translateLastBotQuestionData = [ApiClientConstant.Regimen.Class.FACE, ApiClientConstant.Regimen.Class.HAIR]
      .map((regimenClass: string) => ({ regimenClass, questionId: this.consultationSessionService.getLastBotQuestionId(regimenClass) }))
      .filter((item): boolean => !!item.questionId);
    this.bottomSheet.open(LanguageChangeComponent, {
      data: {
        translateChatMessages: true,
        currentLanguageCode,
        ...(translateLastBotQuestionData.length ? { translateLastBotQuestionData } : {}),
      },
    });
  }
}
